import { useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";

export const Stars = ({ value, onChange, testId, size = "h-7 w-7" }) => (
  <div className="flex gap-1" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onChange} data-testid={`${testId}-${n}`} onClick={() => onChange?.(n)} aria-label={`${n} star${n > 1 ? "s" : ""}`}
        className="transition-transform hover:scale-110 disabled:cursor-default disabled:hover:scale-100">
        <Star className={`${size} ${n <= (value || 0) ? "fill-amber-400 text-amber-400" : "text-zinc-600"}`} />
      </button>
    ))}
  </div>
);

export const StarBadge = ({ rating, count, testId }) => rating ? (
  <span data-testid={testId} className="inline-flex items-center gap-1 text-sm text-amber-200"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />{rating.toFixed(1)}{count != null && <span className="text-xs text-zinc-500">({count})</span>}</span>
) : <span data-testid={testId} className="text-xs text-zinc-500">New</span>;

export const RatingForm = ({ fb, role, onDone }) => {
  const mine = fb.my_rating;
  const [stars, setStars] = useState(mine?.stars || 0);
  const [quality, setQuality] = useState(mine?.quality || 0);
  const [busy, setBusy] = useState(false);
  const locked = mine && !mine.editable;
  const save = async () => {
    setBusy(true);
    try { await api.put(`/trips/${fb.trip_id}/rating`, { stars, quality: role === "customer" ? quality : null }); toast.success("Thanks for your rating"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="lux-card space-y-4 p-5 sm:p-6" data-testid="rating-form">
      <p className="lux-eyebrow">{role === "customer" ? `Rate ${fb.driver_name || "your driver"}` : `Rate ${fb.customer_name}`}</p>
      <Stars value={stars} onChange={locked ? null : setStars} testId="rating-stars" />
      {role === "customer" && (
        <div><p className="mb-2 text-xs text-zinc-400">Quality — vehicle, cleanliness, professionalism</p><Stars value={quality} onChange={locked ? null : setQuality} testId="rating-quality" /></div>
      )}
      {mine && <p data-testid="rating-editable-note" className="text-xs text-zinc-500">{mine.editable ? `You can change this until ${fmtDate(mine.editable_until)}` : "Ratings can only be changed within 24 hours"}</p>}
      {!locked && (
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="rating-submit" onClick={save} disabled={busy || !stars || (role === "customer" && !quality)} className="btn-gold">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{mine ? "Update rating" : "Submit rating"}</button>
          {role === "customer" && !quality && <span data-testid="rating-quality-hint" className="text-xs text-amber-500">{stars ? "Please add a quality score" : "Choose a star rating and a quality score"}</span>}
        </div>
      )}
    </div>
  );
};

export const CommentForm = ({ fb, onDone }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/trips/${fb.trip_id}/comments`, { text }); setText(""); toast.success("Comment saved"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="lux-card space-y-3 p-5 sm:p-6" data-testid="comment-form">
      <p className="lux-eyebrow">Leave a comment</p>
      <textarea data-testid="comment-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} className="lux-input" placeholder="Share a few words about this trip" />
      <div className="flex items-center justify-between text-xs text-zinc-500"><span data-testid="comment-count">{text.trim().length}/1,000</span>
        <button data-testid="comment-submit" onClick={send} disabled={busy} className="btn-outline-gold !py-2 text-sm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Post comment</button></div>
      {fb.comments.map((c) => <p key={c.id} data-testid={`comment-${c.id}`} className="border-t border-white/5 pt-2 text-sm text-zinc-300">{c.text} <span className="text-xs text-zinc-500">· {c.author_name} · {fmtDate(c.created_at)}</span></p>)}
    </div>
  );
};

export const ComplaintForm = ({ fb, onDone }) => {
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const filed = fb.complaints[0];
  const send = async () => {
    setBusy(true);
    try { await api.post(`/trips/${fb.trip_id}/complaints`, { category, description }); toast.success("Complaint filed"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="lux-card space-y-3 p-5 sm:p-6" data-testid="complaint-form">
      <p className="lux-eyebrow">File a complaint</p>
      {filed ? (
        <p data-testid="complaint-filed" className="text-sm text-zinc-300">You filed “{filed.category}” on {fmtDate(filed.created_at)} · status <span className="text-amber-200">{filed.status}</span></p>
      ) : (
        <>
          <select data-testid="complaint-category" value={category} onChange={(e) => setCategory(e.target.value)} className="lux-input">
            <option value="">Choose a category</option>
            {fb.categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <textarea data-testid="complaint-description" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="lux-input" placeholder="What happened?" />
          <button data-testid="complaint-submit" onClick={send} disabled={busy || !category || description.trim().length < 5} className="btn-outline-gold !py-2 text-sm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit complaint</button>
        </>
      )}
    </div>
  );
};
